(() => {
  const normalize = (value) => String(value || '').normalize('NFKC').toLocaleLowerCase('ja');
  const titles = new WeakMap();
  let lastQuery;
  let queryWords = [];
  const matches = (item, state) => {
    if (!titles.has(item)) titles.set(item, normalize(item.title));
    const title = titles.get(item);
    if (state.query !== lastQuery) {
      lastQuery = state.query;
      queryWords = normalize(state.query).trim().split(/\s+/).filter(Boolean);
    }
    return queryWords.every((word) => title.includes(word)) &&
      (state.tags || []).every((tag) => item.tags.includes(tag)) &&
      (state.minPrice == null || item.price >= state.minPrice) &&
      (state.maxPrice == null || item.price <= state.maxPrice) &&
      (!state.inStockOnly || item.available);
  };
  const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[char]);

  const root = window.Shopify?.routes?.root || '/';
  // Short-lived, cross-tab reuse also covers navigation from suggestions to results.
  // Include locale/currency so translated titles and formatted prices never cross contexts.
  const cacheKey = `dhuni-search:v4:${root}:${window.Shopify?.country || ''}:${window.Shopify?.currency?.active || ''}`;
  const cacheLifetime = 60000;
  const validItem = (item) => item && item.id && typeof item.title === 'string' &&
    Array.isArray(item.tags) && item.tags.every((tag) => typeof tag === 'string') &&
    Number.isFinite(item.price) && typeof item.available === 'boolean' &&
    typeof item.url === 'string' && item.url.startsWith('/') && !item.url.startsWith('//');
  const readCache = () => {
    try {
      const raw = window.localStorage.getItem(cacheKey);
      if (!raw || raw.length > 2000000) return null;
      const cached = JSON.parse(raw);
      const age = Date.now() - cached.savedAt;
      if (!Number.isFinite(age) || age < 0 || age >= cacheLifetime ||
        !Array.isArray(cached.items) || cached.items.length > 25000 ||
        !cached.items.every(validItem) || new Set(cached.items.map((item) => item.id)).size !== cached.items.length) return null;
      return cached;
    } catch { return null; }
  };
  // A shared promise avoids downloading the catalog again for each keystroke/search box.
  // Never substitute Shopify's broad search results on an incomplete/failed download.
  let catalogPromise;
  let loadedAt = 0;
  const loadCatalog = () => {
    if (loadedAt && Date.now() - loadedAt >= cacheLifetime) catalogPromise = null;
    if (catalogPromise) return catalogPromise;
    const cached = readCache();
    if (cached) {
      loadedAt = cached.savedAt;
      catalogPromise = Promise.resolve(cached.items);
      return catalogPromise;
    }
    loadedAt = 0;
    catalogPromise = (async () => {
      const readPage = async (page) => {
        const params = new URLSearchParams({ section_id: 'dhuni-search-catalog', page, sort_by: 'title-ascending' });
        const response = await fetch(`${root}collections/all?${params}`, { signal: AbortSignal.timeout(30000) });
        if (!response.ok) throw new Error('Catalog request failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const node = doc.querySelector('[data-dhuni-search-catalog-page]');
        if (!node) throw new Error('Missing catalog');
        const result = JSON.parse(node.textContent);
        if (result.page !== page || !Number.isInteger(result.pages) || result.pages < 0 ||
          !Number.isInteger(result.total) || result.total < 0 || result.total > 25000 ||
          !Array.isArray(result.items)) throw new Error('Invalid catalog page');
        for (const item of result.items) {
          if (!validItem(item)) {
            throw new Error('Invalid catalog item');
          }
        }
        return result;
      };
      const first = await readPage(1);
      const items = [...first.items];
      // Small batches keep large catalogs from flooding the storefront with requests.
      for (let page = 2; page <= first.pages; page += 3) {
        const batch = await Promise.all(Array.from({ length: Math.min(3, first.pages - page + 1) }, (_, i) => readPage(page + i)));
        for (const part of batch) {
          if (part.total !== first.total || part.pages !== first.pages) throw new Error('Catalog changed; retry');
          items.push(...part.items);
        }
      }
      if (items.length !== first.total || new Set(items.map((item) => item.id)).size !== items.length) {
        throw new Error('Incomplete catalog; retry');
      }
      const visible = items.filter((item) => !item.hidden);
      loadedAt = Date.now();
      try {
        const raw = JSON.stringify({ savedAt: loadedAt, items: visible });
        if (raw.length <= 2000000) window.localStorage.setItem(cacheKey, raw);
      } catch { /* Storage may be disabled or full; searching still works. */ }
      return visible;
    })().catch((error) => { catalogPromise = null; throw error; });
    return catalogPromise;
  };
  window.DhuniSearch = { matches, escapeHtml, loadCatalog };
})();
